import { Navigate } from 'react-router-dom';

import { Flex, Spinner } from '@kognitos/lattice';
import {
  sentryCreateBrowserRouter,
  RouteErrorBoundary,
} from '@/modules/sentry';
import Layout from '@/layouts/MainLayout';
import { FullPageLoader } from '@/shared/components';
import { getRoutePattern, PARENT_ROUTES } from '@/routes/util';
import { withProtectedRoute } from '@/shared/auth';

const ProtectedLayout = withProtectedRoute(Layout);

const router = sentryCreateBrowserRouter([
  {
    path: getRoutePattern('HOME'),
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        element: (
          // This is a temporary loading screen for the home route
          // We will redirect user to Chats page from ProtectedLayout HOC
          <Flex justify="center" align="center" className="w-full min-h-screen">
            <Spinner size="xl" variant="default" />
          </Flex>
        ),
      },
      {
        path: getRoutePattern('ONBOARDING'),
        lazy: () => import('@/pages/onboarding/Page'),
      },
      {
        path: getRoutePattern('INVITATION'),
        lazy: () => import('@/pages/onboarding/invitation/Page'),
      },
      {
        path: getRoutePattern('API_KEYS'),
        lazy: () => import('@/pages/api-keys/Page'),
      },
      {
        path: getRoutePattern('PREFERENCES'),
        lazy: () => import('@/pages/preferences/Page'),
      },
      {
        path: getRoutePattern('TUTORIALS'),
        lazy: () => import('@/pages/tutorials/Page'),
      },
      {
        path: getRoutePattern('USER_SETTINGS'),
        lazy: () => import('@/pages/settings/user/Page'),
      },
      {
        path: getRoutePattern('VNC_VIEWER'),
        lazy: () => import('@/pages/vnc-viewer/Page'),
      },
      {
        path: '*',
        lazy: () => import('@/pages/not-found/Page'),
      },
    ],
  },
  // Organization routes (orgId only)
  {
    path: PARENT_ROUTES.ORG,
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        lazy: () => import('@/pages/not-found/Page'),
      },
      {
        path: getRoutePattern('ORGANIZATION_PERFORMANCE'),
        lazy: () => import('@/pages/organization/performance/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS'),
        lazy: () => import('@/pages/remote-connections/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_CREATE'),
        lazy: () => import('@/pages/remote-connections/create/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_DETAIL'),
        lazy: () => import('@/pages/remote-connections/detail/Page'),
      },
      {
        path: getRoutePattern('REMOTE_CONNECTIONS_EDIT'),
        lazy: () => import('@/pages/remote-connections/edit/Page'),
      },
      // Org Settings
      {
        path: getRoutePattern('ORG_SETTINGS'),
        lazy: () => import('@/pages/settings/Page'),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: getRoutePattern('ORG_SETTINGS_OVERVIEW'),
            lazy: () => import('@/pages/settings/org/overview/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_WORKSPACES'),
            lazy: () => import('@/pages/settings/org/workspaces/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_PEOPLE'),
            lazy: () => import('@/pages/settings/org/users/Page'),
          },
          {
            path: getRoutePattern('ORG_SETTINGS_PREFERENCES'),
            lazy: () => import('@/pages/settings/org/preferences/Page'),
          },
        ],
      },
    ],
  },
  // Workspace routes (orgId + workspaceId)
  {
    path: PARENT_ROUTES.WORKSPACE,
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: FullPageLoader,
    children: [
      {
        index: true,
        lazy: () => import('@/pages/not-found/Page'),
      },
      {
        path: getRoutePattern('WORKSPACE_ACCESS_DENIED'),
        lazy: () => import('@/pages/workspace/access-denied/Page'),
      },
      {
        path: getRoutePattern('DRAFTS'),
        lazy: () => import('@/pages/workspace/drafts/Page'),
      },
      {
        path: getRoutePattern('DRAFT_EDITOR'),
        lazy: () => import('@/pages/workspace/draft/edit/Page'),
      },
      {
        path: getRoutePattern('DRAFT_PRE_RUN'),
        lazy: () => import('@/pages/workspace/draft/pre-run/Page'),
      },
      {
        path: getRoutePattern('AUTOMATIONS'),
        lazy: () => import('@/pages/workspace/automations/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_AOP'),
        lazy: () => import('@/pages/workspace/automation/aop/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_DETAILS'),
        lazy: () => import('@/pages/workspace/automation/details/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_PRE_RUN'),
        lazy: () => import('@/pages/workspace/automation/pre-run/Page'),
      },
      {
        path: getRoutePattern('AUTOMATION_RUN'),
        lazy: () => import('@/pages/workspace/automation/run/Page'),
      },
      {
        path: getRoutePattern('DRAFT_RUN'),
        lazy: () => import('@/pages/workspace/draft/run/Page'),
      },
      {
        path: getRoutePattern('LEARNINGS'),
        lazy: () => import('@/pages/workspace/learnings/Page'),
      },
      {
        path: getRoutePattern('GUIDANCE_CENTER'),
        lazy: () => import('@/pages/workspace/guidance-center/Page'),
      },
      {
        path: getRoutePattern('TROUBLESHOOTING_GUIDE'),
        lazy: () => import('@/pages/workspace/troubleshooting-guide/Page'),
      },
      {
        path: getRoutePattern('ACTION_ITEM_DETAIL'),
        lazy: () =>
          import('@/pages/workspace/guidance-center/action-item/Page'),
      },
      {
        path: getRoutePattern('ACTION_ITEM_REVIEW'),
        lazy: () =>
          import('@/pages/workspace/guidance-center/action-item-review/Page'),
      },

      // Integrations routes
      {
        path: getRoutePattern('INTEGRATIONS'),
        lazy: () => import('@/layouts/IntegrationsLayout'),
        children: [
          {
            path: '',
            lazy: () => import('@/layouts/IntegrationsViewLayout'),
            children: [
              {
                index: true,
                lazy: () => import('@/pages/workspace/integrations/all/Page'),
              },
              {
                path: getRoutePattern('INTEGRATIONS_CONNECTIONS'),
                lazy: () =>
                  import('@/pages/workspace/integrations/connections/Page'),
              },
            ],
          },
          {
            path: getRoutePattern('INTEGRATIONS_INTEGRATION_DETAILS'),
            lazy: () =>
              import('@/pages/workspace/integrations/integration-details/Page'),
          },
        ],
      },
      // Workspace Settings
      {
        path: getRoutePattern('WORKSPACE_SETTINGS'),
        lazy: () => import('@/pages/settings/Page'),
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_OVERVIEW'),
            lazy: () => import('@/pages/settings/workspace/overview/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_PEOPLE'),
            lazy: () => import('@/pages/settings/workspace/users/Page'),
          },
          {
            path: getRoutePattern('WORKSPACE_SETTINGS_PREFERENCES'),
            lazy: () => import('@/pages/settings/workspace/preferences/Page'),
          },
        ],
      },
    ],
  },
  {
    path: getRoutePattern('LOGIN'),
    lazy: () => import('@/pages/login/Page'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '*',
    lazy: () => import('@/pages/not-found/Page'),
    errorElement: <RouteErrorBoundary />,
  },
]);

export default router;
